import {
  cloudFunctionService,
  cloudFunctionUrls,
  type CloudFunctionCallResult,
  type CloudFunctionDefinition,
} from '@/lib/cloud-function-service';
import { envConfig } from '@/lib/env-config';
import { gatewayService } from '@/lib/gateway-service';
import { useState } from 'react';
import { useAuthRedux } from './use-auth-redux';
import { useIntegration } from './use-integration';

/** Cloud function names that have been migrated to the gateway API */
const GATEWAY_MIGRATED_FUNCTIONS = [
  'completeRecordingAndStartProcessing',
  'getJobDetailsByJobId',
  'getPublicUrlByFileKey',
  'authenticate',
  'createAiScribeJob',
  'makeCallToIntegrationPartner',
] as const;

type GatewayMigratedFunction = (typeof GATEWAY_MIGRATED_FUNCTIONS)[number];

/** All supported function names (cloud functions + gateway-migrated) */
type SupportedFunctionName =
  | SupportedFunctionName
  | GatewayMigratedFunction;

export interface UseCloudFunctionReturn {
  // State
  loading: boolean;
  error: string | null;
  lastResult: CloudFunctionCallResult | null;

  // Actions
  callFunction: (
    functionName: SupportedFunctionName,
    params: Record<string, unknown>
  ) => Promise<CloudFunctionCallResult>;

  callFunctionWithRetry: (
    functionName: SupportedFunctionName,
    params: Record<string, unknown>
  ) => Promise<CloudFunctionCallResult>;

  callForIntegrationEndpoint: (
    functionName: SupportedFunctionName,
    action: string,
    environment?: string,
    additionalParams?: Record<string, unknown>
  ) => Promise<CloudFunctionCallResult>;

  triggerAuthenticate: () => Promise<CloudFunctionCallResult>;

  getJobDetailsByScribeJobId: (
    scribeJobId: string
  ) => Promise<CloudFunctionCallResult>;
  getJobDetailsByAppointmentInfo: (
    appointmentId: string,
    locationId: string,
    patientId: string
  ) => Promise<CloudFunctionCallResult>;

  makeCallToIntegrationPartner: (
    partnerUrl: string,
    methodType: string,
    token: string,
    locationId: string,
    appointmentId: string,
    jsonString: string
  ) => Promise<CloudFunctionCallResult>;

  // Information
  getRegisteredFunctions: () => CloudFunctionDefinition[];
  getFunctionDefinition: (
    functionName: string
  ) => CloudFunctionDefinition | undefined;
  getCallHistory: () => Array<{
    timestamp: number;
    functionName: string;
    success: boolean;
    responseTime: number;
    error?: string;
  }>;
  getCallStatistics: () => {
    totalCalls: number;
    successfulCalls: number;
    failedCalls: number;
    averageResponseTime: number;
    functionStats: Record<
      string,
      {
        calls: number;
        success: number;
        failure: number;
        avgResponseTime: number;
      }
    >;
  };
  clearCallHistory: () => void;
}

/**
 * Custom hook for Google Cloud Function integration
 * Flexible and scalable hook for handling multiple cloud functions with different parameters
 */
export const useCloudFunction = (): UseCloudFunctionReturn => {
  const { user } = useAuthRedux();
  const { partner, endpoints } = useIntegration();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<CloudFunctionCallResult | null>(
    null
  );

  /**
   * Call a specific cloud function with parameters
   */
  const callFunction = async (
    functionName: SupportedFunctionName,
    params: Record<string, unknown>
  ): Promise<CloudFunctionCallResult> => {
    setLoading(true);
    setError(null);

    try {
      let result: CloudFunctionCallResult;

      // Route migrated functions to gateway API
      if (
        (GATEWAY_MIGRATED_FUNCTIONS as readonly string[]).includes(
          functionName
        )
      ) {
        switch (functionName) {
          case 'completeRecordingAndStartProcessing':
            result = await gatewayService.completeRecording(
              params.scribeJobId as string
            );
            break;
          case 'getJobDetailsByJobId':
            if (params.scribeJobId) {
              result = await gatewayService.getJobDetailsByJobId(
                params.scribeJobId as string
              );
            } else {
              result = await gatewayService.getJobDetailsByAppointmentInfo(
                params.appointmentId as string,
                params.locationId as string,
                params.patientId as string
              );
            }
            break;
          case 'getPublicUrlByFileKey':
            result = await gatewayService.getSignedUrl(
              params.fileKey as string
            );
            break;
          case 'authenticate':
            result = await gatewayService.authenticateIntegration({
              secretId: params.secretId as string,
              username: params.username as string,
              url: params.url as string,
            });
            break;
          case 'createAiScribeJob':
            result = await gatewayService.createScribeJob({
              appointmentId: params.appointmentId as string,
              locationId: params.locationId as string,
              patientId: params.patientId as string,
              firstName: params.firstName as string,
              lastName: params.lastName as string,
              dob: params.dob as string,
              gender: params.gender as string,
              service: params.service as string,
              appointmentStartTime: params.appointmentStartTime as string,
            });
            break;
          case 'makeCallToIntegrationPartner':
            result = await gatewayService.callIntegrationPartner({
              partnerUrl: params.partnerUrl as string,
              methodType: params.methodType as string,
              token: params.token as string,
              locationId: params.locationId as string,
              appointmentId: params.appointmentId as string,
              jsonString: params.jsonString as string,
            });
            break;
          default:
            result = { success: false, error: 'Unknown gateway function' };
        }
      } else {
        result = await cloudFunctionService.callFunction(
          functionName as keyof typeof cloudFunctionUrls,
          params
        );
      }

      setLastResult(result);

      if (!result.success) {
        setError(result.error || 'Function call failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      const result = {
        success: false,
        error: errorMessage,
      };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Call a cloud function with retry logic
   */
  const callFunctionWithRetry = async (
    functionName: SupportedFunctionName,
    params: Record<string, unknown>
  ): Promise<CloudFunctionCallResult> => {
    setLoading(true);
    setError(null);

    try {
      const result = await cloudFunctionService.callFunctionWithRetry(
        functionName as keyof typeof cloudFunctionUrls,
        params
      );
      setLastResult(result);

      if (!result.success) {
        setError(result.error || 'Cloud function call failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      const result = {
        success: false,
        error: errorMessage,
      };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Call cloud function for integration endpoint
   */
  const callForIntegrationEndpoint = async (
    functionName: SupportedFunctionName,
    action: string,
    environment: string = envConfig.env,
    additionalParams: Record<string, unknown> = {}
  ): Promise<CloudFunctionCallResult> => {
    if (!user?.username || !partner?.cloud_secret_id) {
      const error = !user?.username
        ? 'User username not available'
        : 'Integration partner cloud secret ID not available';

      setError(error);
      return {
        success: false,
        error,
      };
    }

    setLoading(true);
    setError(null);

    try {
      const result = await cloudFunctionService.callForIntegrationEndpoint(
        functionName as keyof typeof cloudFunctionUrls,
        partner.cloud_secret_id,
        user.username,
        endpoints,
        action,
        environment,
        additionalParams
      );

      setLastResult(result);

      if (!result.success) {
        setError(result.error || 'Cloud function call failed');
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      const result = {
        success: false,
        error: errorMessage,
      };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Manually trigger authenticate via gateway
   * Calls POST /api/integration/authenticate with {secretId, username, url}
   */
  const triggerAuthenticate = async (): Promise<CloudFunctionCallResult> => {
    if (!user?.username || !partner?.cloud_secret_id) {
      const error = !user?.username
        ? 'User username not available'
        : 'Integration partner cloud secret ID not available';
      setError(error);
      return { success: false, error };
    }

    // Find authenticate endpoint URL from Redux
    const authenticateEndpoint = endpoints.find(
      (ep) => ep.action === 'authenticate' && ep.environment === envConfig.env
    );
    if (!authenticateEndpoint) {
      const error = 'No authenticate endpoint found';
      setError(error);
      return { success: false, error };
    }

    setLoading(true);
    setError(null);
    try {
      const result = await gatewayService.authenticateIntegration({
        secretId: partner.cloud_secret_id,
        username: user.username,
        url: authenticateEndpoint.endpoint_url,
      });
      setLastResult(result);
      if (!result.success) {
        setError(result.error || 'Authentication failed');
      }
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      const result = { success: false, error: errorMessage };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get job details by scribe job ID (via gateway)
   */
  const getJobDetailsByScribeJobId = async (
    scribeJobId: string
  ): Promise<CloudFunctionCallResult> => {
    setLoading(true);
    setError(null);
    try {
      const result = await gatewayService.getJobDetailsByJobId(scribeJobId);
      setLastResult(result);
      if (!result.success) {
        setError(result.error || 'Failed to get job details');
      }
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      const result = { success: false, error: errorMessage };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get job details by appointment information (via gateway)
   */
  const getJobDetailsByAppointmentInfo = async (
    appointmentId: string,
    locationId: string,
    patientId: string
  ): Promise<CloudFunctionCallResult> => {
    setLoading(true);
    setError(null);
    try {
      const result = await gatewayService.getJobDetailsByAppointmentInfo(
        appointmentId,
        locationId,
        patientId
      );
      setLastResult(result);
      if (!result.success) {
        setError(result.error || 'Failed to get job details');
      }
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      const result = { success: false, error: errorMessage };
      setLastResult(result);
      return result;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Make a call to integration partner endpoint
   */
  const makeCallToIntegrationPartner = async (
    partnerUrl: string,
    methodType: string,
    token: string,
    locationId: string,
    appointmentId: string,
    jsonString: string
  ): Promise<CloudFunctionCallResult> => {
    return callFunction('makeCallToIntegrationPartner', {
      partnerUrl,
      methodType,
      token,
      locationId,
      appointmentId,
      jsonString,
    });
  };

  /**
   * Get registered functions
   */
  const getRegisteredFunctions = () => {
    return cloudFunctionService.getRegisteredFunctions();
  };

  /**
   * Get function definition
   */
  const getFunctionDefinition = (functionName: string) => {
    return cloudFunctionService.getFunctionDefinition(functionName);
  };

  /**
   * Get call history
   */
  const getCallHistory = () => {
    return cloudFunctionService.getCallHistory();
  };

  /**
   * Get call statistics
   */
  const getCallStatistics = () => {
    return cloudFunctionService.getCallStatistics();
  };

  /**
   * Clear call history
   */
  const clearCallHistory = () => {
    cloudFunctionService.clearCallHistory();
  };

  return {
    // State
    loading,
    error,
    lastResult,

    // Actions
    callFunction,
    callFunctionWithRetry,
    callForIntegrationEndpoint,
    triggerAuthenticate,
    getJobDetailsByScribeJobId,
    getJobDetailsByAppointmentInfo,
    makeCallToIntegrationPartner,

    // Information
    getRegisteredFunctions,
    getFunctionDefinition,
    getCallHistory,
    getCallStatistics,
    clearCallHistory,
  };
};
